"use client";

import { File, FileSpreadsheet, FileText, Link2, Mail } from "lucide-react";
import type { ComponentType } from "react";

import { Badge } from "@/components/ui/badge";
import type { ArtifactIcon, RunArtifact } from "@/lib/run-artifacts";
import { cn } from "@/lib/utils";

// Generic inline artifact preview card. The collapsed visual model keeps the
// artifact metadata data-driven: title, kind, icon, accent, operation, and links.

const ARTIFACT_ICONS: Record<
  ArtifactIcon,
  ComponentType<{ className?: string }>
> = {
  link: Link2,
  "file-text": FileText,
  "file-spreadsheet": FileSpreadsheet,
  mail: Mail,
  file: File,
};

function OperationBadge({ operation }: { operation: "created" | "updated" }) {
  return (
    <Badge
      variant={operation === "created" ? "success" : "info"}
      className="h-4 gap-1 rounded-full px-1 text-[9px] font-semibold leading-4 normal-case"
    >
      <span className="size-1 rounded-full bg-current" aria-hidden />
      {operation === "created" ? "Created" : "Updated"}
    </Badge>
  );
}

function artifactMeta(artifact: RunArtifact): string[] {
  if (artifact.contentKind === "markdown") {
    return [
      artifact.kindLabel,
      ...(typeof artifact.version === "number" ? [`v${artifact.version}`] : []),
      ...(artifact.pendingVersion ? [`Draft v${artifact.pendingVersion}`] : []),
    ];
  }
  if (artifact.contentKind === "csv") {
    return [
      "Spreadsheet",
      ...(typeof artifact.version === "number" ? [`v${artifact.version}`] : []),
      ...(artifact.pendingVersion ? [`Draft v${artifact.pendingVersion}`] : []),
    ];
  }

  return [
    artifact.kindLabel,
    ...(artifact.href ? ["Link"] : []),
    ...(typeof artifact.version === "number" ? [`v${artifact.version}`] : []),
    ...(artifact.pendingVersion ? [`Draft v${artifact.pendingVersion}`] : []),
  ];
}

export function ArtifactCard({
  artifact,
  className,
  onOpen,
}: {
  artifact: RunArtifact;
  className?: string;
  onOpen?: (artifact: RunArtifact) => void;
}) {
  const Icon = ARTIFACT_ICONS[artifact.icon] ?? File;
  const opensExternally = Boolean(artifact.href && !artifact.contentKind);
  const isButton = Boolean(onOpen) && !opensExternally;
  const Wrapper = opensExternally ? "a" : isButton ? "button" : "div";
  const meta = artifactMeta(artifact);

  return (
    <Wrapper
      {...(opensExternally
        ? {
            href: artifact.href,
            target: "_blank",
            rel: "noreferrer noopener",
          }
        : isButton
          ? { type: "button", onClick: () => onOpen?.(artifact) }
          : {})}
      data-artifact-id={artifact.id}
      className={cn(
        "group/artifact flex h-14 w-full cursor-pointer items-center overflow-hidden rounded-[10px] border border-border/70 bg-surface text-left transition-[border-color,background-color,box-shadow]",
        "hover:border-border hover:shadow-[0_2px_8px_rgba(0,0,0,0.05)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-2",
        className,
      )}
    >
      <span className="flex h-14 w-11 min-w-11 items-center justify-center">
        <span
          className={cn(
            "inline-flex h-[26px] w-[26px] items-center justify-center rounded-[6px] text-white",
            artifact.accentClassName,
          )}
          aria-hidden="true"
        >
          <Icon className="size-3.5" />
        </span>
      </span>

      <span className="flex min-w-0 flex-1 flex-col justify-center gap-[1px] py-2 pr-3">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="min-w-0 flex-1 truncate text-[13px] font-semibold leading-[1.35] text-foreground">
            {artifact.title}
          </span>
          {artifact.operation ? (
            <OperationBadge operation={artifact.operation} />
          ) : null}
        </span>
        {meta.length > 0 ? (
          <span className="flex min-w-0 items-center gap-1 text-[11px] leading-[1.3] text-muted-foreground">
            {meta.map((item, index) => (
              <span key={`${artifact.id}-meta-${item}-${index}`} className="contents">
                {index > 0 ? (
                  <span className="h-0.5 w-0.5 shrink-0 rounded-full bg-border" />
                ) : null}
                <span className="truncate tabular-nums">{item}</span>
              </span>
            ))}
          </span>
        ) : null}
      </span>
    </Wrapper>
  );
}
